/**
 * ExpandableTextarea — a textarea with an expand button that opens
 * a large centered modal editor. Uses a portal so the modal is never
 * constrained by parent overflow/transform.
 */

import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { createPortal } from 'react-dom';
import './ExpandableTextarea.css';

interface ExpandableTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  className?: string;
  label?: string;
  id?: string;
  name?: string;
}

export const ExpandableTextarea: React.FC<ExpandableTextareaProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  rows = 6,
  className = '',
  label,
  id,
  name,
}) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const expandedRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!expanded) setLocalValue(value);
  }, [value, expanded]);

  useEffect(() => {
    if (expanded && expandedRef.current) {
      expandedRef.current.focus();
      expandedRef.current.setSelectionRange(
        expandedRef.current.value.length,
        expandedRef.current.value.length
      );
    }
  }, [expanded]);

  const handleSave = () => {
    onChange(localValue);
    setExpanded(false);
  };

  const handleCancel = () => {
    setLocalValue(value);
    setExpanded(false);
  };

  return (
    <>
      <div className="expandable-textarea-wrapper">
        <textarea dir="auto"
          id={id}
          name={name}
          className={`form-input context-prompt-textarea ${className}`}
          rows={rows}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <button
          type="button"
          className="expand-btn"
          onClick={() => setExpanded(true)}
          title={t('common.expandEditor')}
          disabled={disabled}
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      </div>

      {expanded && createPortal(
        <div className="expand-overlay">
          <div className="expand-modal" onClick={(e) => e.stopPropagation()}>
            <div className="expand-modal-header">
              <h3 className="expand-modal-title">{label}</h3>
              <div className="expand-modal-actions">
                <button type="button" className="expand-btn-cancel" onClick={handleCancel}>
                  {t('vault.cancel')}
                </button>
                <button type="button" className="expand-btn-save" onClick={handleSave}>
                  {t('superAdmin.cascade.applyConfirmOk')}
                </button>
              </div>
            </div>
            <textarea dir="auto"
              ref={expandedRef}
              className="expand-modal-textarea"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
